import { scopeProps } from "@/lib/controlAnalytics";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import MarkSentDialog from "@/components/applications/MarkSentDialog";
import SkipDialog from "@/components/applications/SkipDialog";
import JobDrawer from "@/components/jobs/JobDrawer";
import JobsCardList from "@/components/jobs/JobsCardList";
import JobsFilters from "@/components/jobs/JobsFilters";
import JobsPager from "@/components/jobs/JobsPager";
import JobsResults from "@/components/jobs/JobsResults";
import JobsToolbar from "@/components/jobs/JobsToolbar";
import { useGate } from "@/components/site/gateContext";
import type { Application } from "@/lib/api/envoy";
import { isNowhere, type useJobsSearch } from "@/lib/hooks/jobsSearch";
import { useDrawerBeside } from "@/lib/hooks/viewport";
import {
    boardEmptyLine,
    drawerPosting,
    fitIn,
    hasFilters,
    jobsSorts,
    shownOnBoard,
    showsPager,
    sortOf,
    statusCounts,
    tabCounts,
} from "@/lib/jobs";
import { postingDetailPath } from "@/lib/routes";
import { skippingOf, type Skipping } from "@/lib/skipping";

/**
 * The board under a page's own header: the filters, the count and the
 * order, the results with the open posting in the drawer beside them,
 * and what follows them. A posting opens in the drawer (`?posting=`), or
 * on its own page. With none chosen, the first on the page opens there.
 * On mobile there is no drawer: a posting opens on its own page.
 */
export default function JobsBoard({
    search,
    member,
    statusTabs,
    showSave,
    visitor,
    line,
    linkCompanies = true,
    header,
    footer,
}: {
    search: ReturnType<typeof useJobsSearch>;
    member: boolean;
    /**
     * The status tabs over the results: for a member, and while a likely
     * sign-in is read, so the board does not drop when they land.
     */
    statusTabs: boolean;
    showSave: boolean;
    /** The look of a board for a visitor, with Get new matches by email. */
    visitor: boolean;
    /** The words beside the count. Null where the header says how many. */
    line: string | null;
    /** Each card's company leads to its jobs. Off on one company's own page. */
    linkCompanies?: boolean;
    header: ReactNode;
    footer?: ReactNode;
}) {
    const { ask } = useGate();
    const { view, update, pager, board, postings, applications, fits } = search;
    const [sending, setSending] = useState<Application | null>(null);
    const [skipping, setSkipping] = useState<Skipping | null>(null);
    const [closed, setClosed] = useState(false);
    const beside = useDrawerBeside();
    const navigate = useNavigate();
    const now = new Date();

    const shown = shownOnBoard(search.source, postings, view.status, applications.data);
    const open = drawerPosting({
        chosen: view.posting,
        onPage: search.onPage,
        elsewhere: search.elsewhere,
        shown,
        closed,
        beside,
    });
    const total = board.total;

    // A link to an open posting lands on its own page where there is no drawer.
    const chosen = search.onPage ?? search.elsewhere;
    useEffect(() => {
        if (!beside && chosen)
            void navigate(postingDetailPath(chosen.posting_id), { replace: true });
    }, [beside, chosen, navigate]);

    return (
        <div
            {...scopeProps({
                has_query: Boolean(view.q),
                has_filters: hasFilters(view) || search.place !== null,
                count: shown.length,
            })}
            className="-mt-1 flex flex-col gap-[18px]"
        >
            {header}
            <JobsFilters
                view={view}
                member={statusTabs}
                statusCounts={tabCounts(
                    applications.isSuccess
                        ? statusCounts(search.cataloguePostings, applications.data)
                        : undefined,
                    search.totals,
                )}
                envoyCounts={search.totals !== null}
                counts={search.counts.data}
                nowhere={search.nowhere}
                onChange={update}
            />
            <JobsToolbar
                total={total}
                line={line}
                visitor={visitor}
                onAlerts={() => ask("alerts")}
                sorts={jobsSorts(view)}
                sort={sortOf(view)}
                onSort={(sort) => update({ sort })}
            />

            <div className="flex flex-wrap items-start gap-5">
                <JobsResults
                    // A distance refused for want of a place is asked again
                    // without it, not shown as a failure.
                    error={board.error && !isNowhere(board.error) ? board.error : null}
                    pending={board.pending}
                    empty={
                        shown.length === 0
                            ? boardEmptyLine(
                                  search.source,
                                  view.status,
                                  postings.length,
                                  total,
                                  search.company,
                              )
                            : null
                    }
                    pager={
                        showsPager(board, pager.hasPrevious) && (
                            <JobsPager
                                pager={pager}
                                found={board.found}
                                total={total}
                                nextCursor={board.nextCursor}
                                stale={board.stale}
                            />
                        )
                    }
                >
                    <JobsCardList
                        postings={shown}
                        applications={applications.data}
                        fits={fits}
                        now={now}
                        openId={open?.posting_id}
                        member={member}
                        showSave={showSave}
                        linkCompanies={linkCompanies}
                        onOpen={(posting) =>
                            beside ? update({ posting }) : void navigate(postingDetailPath(posting))
                        }
                    />
                </JobsResults>

                {open && (
                    <JobDrawer
                        key={open.posting_id}
                        posting={open}
                        member={member}
                        application={applications.data?.[open.posting_id]}
                        fit={fitIn(fits, open.posting_id)}
                        onClose={() => {
                            setClosed(true);
                            update({ posting: null });
                        }}
                        onSent={setSending}
                        onSkip={() =>
                            setSkipping(skippingOf(applications.data?.[open.posting_id], open))
                        }
                    />
                )}
            </div>

            {footer}

            <MarkSentDialog application={sending} onClose={() => setSending(null)} />
            <SkipDialog
                application={skipping?.application ?? null}
                posting={skipping?.posting ?? null}
                onClose={() => setSkipping(null)}
            />
        </div>
    );
}
